/**
 * Google Drive Helper Functions for Registration Documents
 * 
 * These utilities handle file access, metadata, and operations for the new 
 * club-based Google Drive document storage system.
 */

import { auth } from '../firebaseConfig';

/**
 * Get the secure file URL for a registration document
 * @param {string} applicationId - The application ID
 * @param {string} documentType - The document type (photoId, medicalClearance, etc.)
 * @param {number} fileIndex - The file index (default: 0)
 * @returns {string} The secure file access URL
 */
export const getFileUrl = (applicationId, documentType, fileIndex = 0) => {
  // Use the same base URL logic as the existing API client
  const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
  const path = `/api/registration/applications/${applicationId}/documents/${documentType}/files/${fileIndex}`;
  

  
  // Always construct full URL like the existing API client does
  return baseUrl + path;
};

/**
 * Get file metadata from the backend
 * @param {string} applicationId - The application ID
 * @param {string} documentType - The document type
 * @param {number} fileIndex - The file index
 * @returns {Promise<Object>} File metadata object
 */
export const getFileMetadata = async (applicationId, documentType, fileIndex = 0) => {
  try {
    // Get Firebase auth token
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    
    const token = await currentUser.getIdToken();
    
    const response = await fetch(
      `/api/registration/applications/${applicationId}/documents/${documentType}/files/${fileIndex}/metadata`,
      {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to get file metadata: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting file metadata:', error);
    throw error;
  }
};

/**
 * Check if a file has Google Drive storage information
 * @param {Object} file - The file object from the application
 * @returns {boolean} True if file is stored in Google Drive
 */
export const hasGoogleDriveFile = (file) => {
  return !!(file && file.driveFileId && file.driveFileName);
};

/**
 * Check if a file is legacy (stored locally, deprecated)
 * @param {Object} file - The file object from the application
 * @returns {boolean} True if file is legacy local storage
 */
export const isLegacyFile = (file) => {
  return !!(file && file.storagePath && !file.driveFileId);
};

/**
 * Get a human-readable file status
 * @param {Object} file - The file object
 * @returns {Object} Status information with icon, label, and className
 */
export const getFileStatus = (file) => {
  if (!file) {
    return { status: 'missing', icon: '❌', label: 'Missing', className: 'status-missing' };
  }

  switch (file.status) {
    case 'verified':
      return { status: 'verified', icon: '✅', label: 'Verified', className: 'status-verified' };
    case 'pending':
      return { status: 'pending', icon: '⏳', label: 'Pending Review', className: 'status-pending' };
    case 'rejected':
      return { status: 'rejected', icon: '❌', label: 'Rejected', className: 'status-rejected' };
    case 'expired':
      return { status: 'expired', icon: '⏰', label: 'Expired', className: 'status-expired' };
    default:
      return { status: 'unknown', icon: '❓', label: 'Unknown', className: 'status-unknown' };
  }
};

/**
 * Get the overall document status for a document with multiple files
 * @param {Object} document - The document object with files array
 * @returns {Object} Overall status information
 */
export const getDocumentStatus = (document) => {
  if (!document || !document.files || document.files.length === 0) {
    return { status: 'missing', icon: '❌', label: 'No Files', className: 'status-missing' };
  }

  const files = document.files;
  
  // If all files are verified
  if (files.every(f => f.status === 'verified')) {
    return { status: 'verified', icon: '✅', label: 'All Verified', className: 'status-verified' };
  }
  
  // If any file is rejected
  if (files.some(f => f.status === 'rejected')) {
    return { status: 'rejected', icon: '❌', label: 'Issues Found', className: 'status-rejected' };
  }
  
  // If any file is expired
  if (files.some(f => f.status === 'expired')) {
    return { status: 'expired', icon: '⏰', label: 'Expired', className: 'status-expired' };
  }
  
  // If all files are pending
  if (files.every(f => f.status === 'pending')) {
    return { status: 'pending', icon: '⏳', label: 'Under Review', className: 'status-pending' };
  }
  
  // Mixed statuses
  return { status: 'mixed', icon: '🔄', label: 'Mixed Status', className: 'status-mixed' };
};

/**
 * Format file size in a human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "1.2 MB", "345 KB")
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return 'Unknown size';
  
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

/**
 * Format upload date in a user-friendly format
 * @param {string|Date} dateString - The date string or Date object
 * @returns {string} Formatted date
 */
export const formatUploadDate = (dateString) => {
  if (!dateString) return 'Unknown date';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return 'Invalid date';
  }
};

/**
 * Get the Google Drive folder path for display
 * @param {Object} file - The file object
 * @returns {string} Formatted folder path or fallback
 */
export const getGoogleDrivePath = (file) => {
  if (file && file.driveFullPath) {
    // Extract just the folder path without the filename
    const pathParts = file.driveFullPath.split('/');
    pathParts.pop(); // Remove filename
    
    // Shorten the path for better display
    if (pathParts.length > 4) {
      // Show first part, ellipsis, and last 2 parts
      const start = pathParts[0];
      const end = pathParts.slice(-2);
      return `${start} / ... / ${end.join(' / ')}`;
    }
    
    return pathParts.join(' / ');
  }
  
  if (hasGoogleDriveFile(file)) {
    return 'Google Drive';
  }
  
  if (isLegacyFile(file)) {
    return 'Local Storage (Legacy)';
  }
  
  return 'Unknown Location';
};

/**
 * Helper function to trigger file download
 * @param {Blob} blob - The blob object
 * @param {string} fileName - The file name
 */
const downloadFile = (blob, fileName) => {
  try {
    
    // Verify blob is valid
    if (!blob || blob.size === 0) {
      throw new Error('Invalid or empty file data');
    }
    
    // Use different download methods based on browser support
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      // Internet Explorer
      window.navigator.msSaveOrOpenBlob(blob, fileName);
      return;
    }
    
    // Modern browsers
    const downloadUrl = window.URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName || 'document';
    link.style.display = 'none';
    
    // Add to DOM temporarily
    document.body.appendChild(link);
    
    // Trigger download
    link.click();
    
    // Clean up immediately after click
    document.body.removeChild(link);
    
    // Schedule URL cleanup after download should have started
    setTimeout(() => {
      window.URL.revokeObjectURL(downloadUrl);
    }, 5000);
    
      } catch (error) {
    console.error('Download failed:', error);
    throw new Error(`Download failed: ${error.message}`);
  }
};

/**
 * Safely view a document with proper authentication and error handling
 * @param {string} applicationId - The application ID
 * @param {string} documentType - The document type
 * @param {number} fileIndex - The file index
 * @param {string} fileName - The original file name (for display)
 * @param {Function} onError - Optional error callback
 * @returns {Promise<void>}
 */
export const viewDocument = async (applicationId, documentType, fileIndex, fileName, onError) => {
  try {
    
    // Get Firebase auth token
    let authToken = '';
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('You must be logged in to view documents.');
      }
      authToken = await currentUser.getIdToken();
    } catch (authError) {
      console.warn('❌ Failed to get auth token:', authError);
      const errorMsg = 'Authentication failed. Please log in again to view documents.';
      if (onError) onError(errorMsg);
      else alert(errorMsg);
      return;
    }

    const url = getFileUrl(applicationId, documentType, fileIndex);

    // Try to fetch the file with authentication headers
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    });

    if (response.ok) {
      // Get the blob and create object URL for viewing
      const blob = await response.blob();
      

      
      // If we got HTML instead of the expected file type, it's likely an error page
      if (blob.type === 'text/html') {
        const errorMsg = 'Server returned an HTML page instead of the file. Please try again or contact support.';
        if (onError) onError(errorMsg);
        else alert(errorMsg);
        return;
      }
      
      // Verify blob size
      if (blob.size === 0) {
        const errorMsg = 'Downloaded file is empty. There may be an issue with the file storage.';
        if (onError) onError(errorMsg);
        else alert(errorMsg);
        return;
      }
      
      const objectUrl = window.URL.createObjectURL(blob);
      
      // For PDF files, try to open in new tab first
      if (blob.type === 'application/pdf') {
        const newWindow = window.open(objectUrl, '_blank', 'noopener,noreferrer');
        
        // Give the window a moment to load, then check if it's still available
        setTimeout(() => {
          try {
            if (!newWindow || newWindow.closed) {
              console.log('PDF window was blocked or closed, trying download fallback');
              downloadFile(blob, fileName);
            }
          } catch (error) {
            // Can't access window properties due to cross-origin, assume it opened successfully
            console.log('PDF window opened (cross-origin check failed, assuming success)');
          }
        }, 100);
      } else {
        // For images and other files, try inline viewing first
        try {
          const newWindow = window.open('', '_blank', 'noopener,noreferrer');
          if (newWindow) {
            newWindow.document.write(`
              <!DOCTYPE html>
              <html>
                <head>
                  <title>${fileName}</title>
                  <style>
                    body { margin: 0; padding: 20px; background: #f0f0f0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
                    img { max-width: 100%; max-height: 100%; object-fit: contain; box-shadow: 0 4px 8px rgba(0,0,0,0.1); }
                    .container { text-align: center; }
                    .filename { margin-bottom: 20px; font-family: Arial, sans-serif; color: #333; }
                    .download-link { display: inline-block; margin-top: 20px; padding: 10px 20px; background: #007bff; color: white; text-decoration: none; border-radius: 4px; }
                  </style>
                </head>
                <body>
                  <div class="container">
                    <div class="filename">${fileName} (${(blob.size / 1024).toFixed(1)} KB)</div>
                    ${blob.type.includes('image') 
                      ? `<img src="${objectUrl}" alt="${fileName}" onerror="this.style.display='none'; document.querySelector('.error-msg').style.display='block';">`
                      : `<p>Preview not available for this file type.</p>`
                    }
                    <div class="error-msg" style="display: none;">
                      <p>Unable to display file.</p>
                    </div>
                    <a href="${objectUrl}" download="${fileName}" class="download-link">Download File</a>
                  </div>
                </body>
              </html>
            `);
            newWindow.document.close();
          } else {
            // Fallback to download
            downloadFile(blob, fileName);
          }
        } catch (error) {
          console.error('Error creating custom viewer:', error);
          downloadFile(blob, fileName);
        }
      }
      
      // Clean up the object URL after a longer delay for viewing
      setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 600000); // Clean up after 10 minutes for viewing
      
    } else {
      // Handle different error cases
      let errorMsg = 'Failed to load document.';
      
      if (response.status === 401) {
        errorMsg = 'Authentication failed. Please log in again to view documents.';
      } else if (response.status === 403) {
        errorMsg = 'You do not have permission to view this document.';
      } else if (response.status === 404) {
        errorMsg = 'Document not found. It may have been deleted or moved.';
      } else {
        errorMsg = `Failed to load document: ${response.statusText || 'Unknown error'}`;
      }
      
      console.error('File fetch failed:', response.status, response.statusText);
      if (onError) onError(errorMsg);
      else alert(errorMsg);
    }
  } catch (error) {
    console.error('Error viewing document:', error);
    const errorMsg = `Error loading document: ${error.message}`;
    if (onError) onError(errorMsg);
    else alert(errorMsg);
  }
};

/**
 * Update file status (for admin use)
 * @param {string} applicationId - The application ID
 * @param {string} documentType - The document type
 * @param {number} fileIndex - The file index
 * @param {string} newStatus - The new status (pending, verified, rejected, expired)
 * @param {string} notes - Optional notes for the status change
 * @returns {Promise<Object>} Updated file object
 */
export const updateFileStatus = async (applicationId, documentType, fileIndex, newStatus, notes = '') => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    
    const token = await currentUser.getIdToken();
    
    const response = await fetch(
      `/api/registration/applications/${applicationId}/documents/${documentType}/files/${fileIndex}/status`,
      {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus, notes })
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to update file status: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating file status:', error);
    throw error;
  }
};

/**
 * Get all document types for an application
 * @param {Object} application - The application object
 * @returns {Array<string>} Array of document types (requirementIds)
 */
export const getDocumentTypes = (application) => {
  if (!application || !application.documents) return [];
  return application.documents.map(doc => doc.requirementId);
};

/**
 * Find a specific document by type
 * @param {Object} application - The application object
 * @param {string} documentType - The document type to find
 * @returns {Object|null} The document object or null if not found
 */
export const findDocument = (application, documentType) => {
  if (!application || !application.documents) return null;
  return application.documents.find(doc => doc.requirementId === documentType) || null;
};

/**
 * Get the total number of files across all documents in an application
 * @param {Object} application - The application object
 * @returns {number} Total file count
 */
export const getTotalFileCount = (application) => {
  if (!application || !application.documents) return 0;
  return application.documents.reduce((total, doc) => {
    return total + (doc.files ? doc.files.length : 0);
  }, 0);
};

/**
 * Get statistics about document completion
 * @param {Object} application - The application object
 * @returns {Object} Statistics object with counts and percentages
 */
export const getDocumentStats = (application) => {
  if (!application || !application.documents) {
    return {
      totalDocuments: 0,
      totalFiles: 0,
      verifiedFiles: 0,
      pendingFiles: 0,
      rejectedFiles: 0,
      missingDocuments: 0,
      completionPercentage: 0
    };
  }

  let totalFiles = 0;
  let verifiedFiles = 0;
  let pendingFiles = 0;
  let rejectedFiles = 0;
  let missingDocuments = 0;

  application.documents.forEach(doc => {
    if (!doc.files || doc.files.length === 0) {
      missingDocuments++;
    } else {
      totalFiles += doc.files.length;
      doc.files.forEach(file => {
        switch (file.status) {
          case 'verified':
            verifiedFiles++;
            break;
          case 'pending':
            pendingFiles++;
            break;
          case 'rejected':
            rejectedFiles++;
            break;
        }
      });
    }
  });

  const completionPercentage = totalFiles > 0 
    ? Math.round((verifiedFiles / totalFiles) * 100) 
    : 0;

  return {
    totalDocuments: application.documents.length,
    totalFiles,
    verifiedFiles,
    pendingFiles,
    rejectedFiles,
    missingDocuments,
    completionPercentage
  };
};


